import { Check, Loader2, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { multimodalApi, type SketchFigure, type SketchFrame, type SketchPoolCandidate } from '@/api/multimodalApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

const slots = ['L-front', 'C-front', 'R-front', 'L-mid', 'C-mid', 'R-mid', 'L-back', 'C-back', 'R-back']
const depths = ['foreground', 'midground', 'background']
const gazes = ['camera', 'off_left', 'off_right', 'up', 'down']
const postures = ['standing', 'sitting', 'leaning', 'crouching', 'lying', 'walking', 'running']

export function SketchReviewPanel({
  frames,
  updatingPanelId,
  confirming,
  rerenderingPanelId,
  selectingSketchCandidate,
  onUpdateFigure,
  onConfirmAll,
  onRenderPanel,
  onSelectSketchCandidate,
}: {
  frames: SketchFrame[]
  updatingPanelId: string | null
  confirming: boolean
  rerenderingPanelId: string | null
  selectingSketchCandidate?: { panelId: string; assetId: string } | null
  onUpdateFigure: (panelId: string, figure: SketchFigure) => void
  onConfirmAll: () => void
  onRenderPanel: (panelId: string) => void
  onSelectSketchCandidate?: (panelId: string, assetId: string) => void
}) {
  const { t } = useTranslation()
  if (frames.length === 0) {
    return <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">{t('sketchReview.empty')}</div>
  }

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold tracking-normal">{t('sketchReview.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('sketchReview.subtitle')}</p>
        </div>
        <Button type="button" size="sm" onClick={onConfirmAll} disabled={confirming}>
          <Check className="size-4" />
          {t('sketchReview.confirmAll')}
        </Button>
      </div>
      <div className="grid gap-3">
        {frames.map((frame, index) => (
          <article key={frame.panel_id} className="rounded-md border p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-sm font-medium">{t('sketchReview.panelNo', { index: index + 1 })}</span>
                <Badge variant="outline">{frame.shot_type}</Badge>
                {frame.frame_role ? <Badge variant="secondary">{frame.frame_role}</Badge> : null}
                <Badge variant={frame.status === 'pending' ? 'destructive' : 'outline'}>{t(`sketchReview.status.${frame.status}`, { defaultValue: frame.status })}</Badge>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onRenderPanel(frame.panel_id)}
                disabled={rerenderingPanelId === frame.panel_id}
              >
                <RefreshCw className="size-4" />
                {frame.thumbnail_ref ? t('sketchReview.rerenderPanel') : t('sketchReview.renderSketch')}
              </Button>
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(18rem,0.85fr)_minmax(18rem,1fr)]">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <SketchSvg frame={frame} />
                <SketchThumbnail frame={frame} />
              </div>
              <div className="space-y-3">
                {frame.spatial_anchor ? (
                  <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">{frame.spatial_anchor}</p>
                ) : null}
                <SketchVLMResult frame={frame} />
                <SketchPoolCandidates
                  panelId={frame.panel_id}
                  candidates={frame.pool_candidates ?? []}
                  selectingCandidate={selectingSketchCandidate?.panelId === frame.panel_id ? selectingSketchCandidate.assetId : null}
                  onSelect={(assetId) => onSelectSketchCandidate?.(frame.panel_id, assetId)}
                />
                {frame.figures.map((figure) => (
                  <FigureControls
                    key={figure.character_id}
                    figure={figure}
                    disabled={updatingPanelId === frame.panel_id}
                    onChange={(next) => onUpdateFigure(frame.panel_id, next)}
                  />
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

export function SketchPoolCandidates({
  panelId,
  candidates,
  selectingCandidate,
  onSelect,
}: {
  panelId: string
  candidates: SketchPoolCandidate[]
  selectingCandidate: string | null
  onSelect: (assetId: string) => void
}) {
  const { t } = useTranslation()
  const sorted = [...candidates].sort((a, b) => b.score - a.score || a.rank - b.rank)
  if (sorted.length <= 1) return null
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-xs font-semibold tracking-normal">{t('sketchReview.pool.title')}</h4>
        <Badge variant="secondary" className="rounded-md">
          {t('sketchReview.pool.count', { count: sorted.length })}
        </Badge>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {sorted.map((candidate) => {
          const selecting = selectingCandidate === candidate.asset_id
          return (
            <button
              key={candidate.asset_id}
              type="button"
              className={cn(
                'overflow-hidden rounded-md border bg-background text-left transition hover:border-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                candidate.primary ? 'border-primary shadow-sm ring-1 ring-primary/40' : 'border-border',
              )}
              disabled={candidate.primary || Boolean(selectingCandidate)}
              onClick={() => onSelect(candidate.asset_id)}
              aria-label={t('sketchReview.pool.selectCandidate', { rank: candidate.rank })}
            >
              <img
                src={multimodalApi.panelPoolAssetImageUrl(panelId, candidate.asset_id)}
                alt={t('sketchReview.pool.candidateAlt', { rank: candidate.rank })}
                loading="lazy"
                className="aspect-video w-full bg-muted object-cover"
              />
              <div className="space-y-2 p-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge variant={candidate.primary ? 'default' : 'outline'} className="rounded-md">
                    {candidate.primary ? <Check className="size-3" /> : null}
                    {candidate.primary ? t('sketchReview.pool.primary') : `#${candidate.rank}`}
                  </Badge>
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('sketchReview.pool.score', { score: Math.round(candidate.score) })}
                  </span>
                </div>
                {!candidate.primary ? (
                  <span className="inline-flex h-6 w-full items-center justify-center gap-1 rounded-md border bg-background px-2 text-xs font-medium">
                    {selecting ? <Loader2 className="size-3 animate-spin" /> : null}
                    {t('sketchReview.pool.useAsPrimary')}
                  </span>
                ) : null}
              </div>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function SketchSvg({ frame }: { frame: SketchFrame }) {
  return (
    <svg viewBox="0 0 100 60" role="img" aria-label={frame.panel_id} className="aspect-video w-full rounded-md border bg-muted/20">
      {frame.spatial_anchor ? <rect x="38" y="22" width="24" height="12" fill="none" stroke="currentColor" strokeDasharray="3 3" opacity="0.45" /> : null}
      {frame.figures.map((figure) => {
        const [x1, y1, x2, y2] = figure.bbox ?? [0.4, 0.2, 0.6, 0.8]
        const cx = ((x1 + x2) / 2) * 100
        const top = y1 * 60
        const bottom = y2 * 60
        const headR = Math.max(3, (x2 - x1) * 100 * 0.18)
        const bodyW = Math.max(7, (x2 - x1) * 100 * 0.45)
        const arrow = facingArrow(figure.facing, cx, top + headR)
        return (
          <g key={figure.character_id}>
            <circle cx={cx} cy={top + headR} r={headR} fill="currentColor" opacity="0.72" />
            <rect x={cx - bodyW / 2} y={top + headR * 2 + 1} width={bodyW} height={Math.max(8, bottom - top - headR * 2 - 1)} rx="1.5" fill="currentColor" opacity="0.34" />
            <line x1={cx} y1={top + headR} x2={arrow.x} y2={arrow.y} stroke="currentColor" strokeWidth="1.5" />
            <text x={cx} y={Math.min(57, bottom + 5)} textAnchor="middle" className="fill-current text-[4px]">
              {figure.character_id}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

function SketchThumbnail({ frame }: { frame: SketchFrame }) {
  const { t } = useTranslation()
  if (!frame.thumbnail_ref) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed bg-muted/20 text-xs text-muted-foreground">
        {t('sketchReview.thumbnailPending')}
      </div>
    )
  }
  return (
    <figure className="overflow-hidden rounded-md border bg-muted/20">
      <img
        src={multimodalApi.sketchThumbnailUrl(frame.panel_id)}
        alt={t('sketchReview.thumbnailAlt')}
        loading="lazy"
        className="aspect-video w-full object-cover"
      />
    </figure>
  )
}

function SketchVLMResult({ frame }: { frame: SketchFrame }) {
  const { t } = useTranslation()
  const result = frame.vlm_result
  if (!result?.assessment && !result?.issues?.length) return null
  const score = typeof result.assessment?.score === 'number' ? result.assessment.score : undefined
  const passed = result.assessment?.status === 'passed'
  const issues = result.issues ?? []
  return (
    <div className="rounded-md border px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{t('sketchReview.vlmTitle')}</span>
        {score !== undefined ? <Badge variant={passed ? 'outline' : 'destructive'}>{t('sketchReview.vlmScore', { score })}</Badge> : null}
      </div>
      {issues.length > 0 ? (
        <ul className="mt-2 space-y-1 text-muted-foreground">
          {issues.map((issue, index) => (
            <li key={`${issue.code ?? 'issue'}-${index}`}>{issue.message || issue.code || t('sketchReview.vlmIssueFallback')}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function FigureControls({
  figure,
  disabled,
  onChange,
}: {
  figure: SketchFigure
  disabled: boolean
  onChange: (figure: SketchFigure) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="text-sm font-medium">{figure.character_id}</div>
      <div>
        <Label className="text-xs">{t('sketchReview.slot')}</Label>
        <div className="mt-1 grid grid-cols-3 gap-1">
          {slots.map((slot) => (
            <Button
              key={slot}
              type="button"
              size="sm"
              variant={figure.slot === slot ? 'default' : 'outline'}
              disabled={disabled}
              onClick={() => onChange({ ...figure, slot })}
              className="h-8 px-1 text-xs"
            >
              {slot}
            </Button>
          ))}
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <IntentSelect label={t('sketchReview.depth')} value={figure.depth ?? ''} options={depths} disabled={disabled} onChange={(depth) => onChange({ ...figure, depth })} />
        <IntentSelect label={t('sketchReview.gaze')} value={figure.gaze ?? ''} options={gazes} disabled={disabled} onChange={(gaze) => onChange({ ...figure, gaze })} />
        <IntentSelect label={t('sketchReview.posture')} value={figure.posture ?? ''} options={postures} disabled={disabled} onChange={(posture) => onChange({ ...figure, posture })} />
      </div>
    </div>
  )
}

function IntentSelect({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <label className="space-y-1 text-xs">
      <span className="font-medium text-muted-foreground">{label}</span>
      <select
        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  )
}

function facingArrow(facing: string | undefined, cx: number, cy: number) {
  switch (facing) {
    case 'left':
      return { x: cx - 8, y: cy }
    case 'right':
      return { x: cx + 8, y: cy }
    case 'up':
      return { x: cx, y: cy - 7 }
    case 'down':
      return { x: cx, y: cy + 7 }
    default:
      return { x: cx, y: cy - 6 }
  }
}
